import React, { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { UserPlus, Search, Shield, CheckCircle2, User, Mail, Phone, Building, Briefcase, Users, X, AtSign, Hash, MessageSquare, Crown, HardHat, PenTool, Wallet, Eye } from 'lucide-react';
import { addProjectMember, addMemberFromMarketplace, fetchProjectMembers } from '../../api/projectMember';
import { MarketplaceSearch, type UserSearchItem } from './MarketplaceSearch';
import toast from 'react-hot-toast';
import Modal from '../Modal';

const CANONICAL_ROLES = [
  { value: 'owner', label: 'Owner', desc: 'Pemilik proyek (BUMN/Swasta)', icon: Crown },
  { value: 'manager', label: 'Project Manager', desc: 'Project Manager / Lead', icon: Briefcase },
  { value: 'contractor', label: 'Contractor', desc: 'Pelaksana teknis / Kontraktor', icon: HardHat },
  { value: 'architect', label: 'Architect', desc: 'Perencana / Arsitek', icon: PenTool },
  { value: 'finance', label: 'Finance', desc: 'Pengelola Keuangan', icon: Wallet },
  { value: 'viewer', label: 'Viewer', desc: 'Akses lihat saja', icon: Eye },
];

interface AddMemberFormProps {
  projectId: string;
  projectName?: string;
  onSuccess?: () => void;
}

export const AddMemberForm: React.FC<AddMemberFormProps> = ({ projectId, projectName = "Proyek MANPRO", onSuccess }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'manual' | 'marketplace'>('manual');
  const [confirmType, setConfirmType] = useState<'email' | 'whatsapp' | null>(null);

  // Manual Form State
  const [manualForm, setManualForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    company: '',
    job_title: '',
  });

  // Marketplace State
  const [selectedUsers, setSelectedUsers] = useState<UserSearchItem[]>([]);
  const [marketplaceRole, setMarketplaceRole] = useState('');
  const [isWALoading, setIsWALoading] = useState(false);

  // Fetch all members to check if owner/manager already exists
  const { data: allMembersData } = useQuery({
    queryKey: ['projectMembers', projectId, 'all'],
    queryFn: () => fetchProjectMembers(projectId, 1, 1000),
    staleTime: 1000 * 30,
  });

  const existingMembers = allMembersData?.data || [];
  const hasOwner = existingMembers.some((m: any) => m.role === 'owner' && m.status !== 'inactive' && m.status !== 'removed');
  const hasManager = existingMembers.some((m: any) => m.role === 'manager' && m.status !== 'inactive' && m.status !== 'removed');

  const availableRoles = CANONICAL_ROLES.filter((r) => {
    if (r.value === 'owner' && hasOwner) return false;
    if (r.value === 'manager' && hasManager) return false;
    return true;
  });

  const manualMut = useMutation({
    mutationFn: () => addProjectMember(projectId, { ...manualForm, via: 'email' }),
    onSuccess: (data) => {
      const isPending = data?.content?.status === 'pending' || data?.data?.status === 'pending';
      setConfirmType(null);
      toast.success(isPending ? 'Undangan terkirim' : 'Anggota berhasil ditambahkan');
      queryClient.invalidateQueries({ queryKey: ['projectMembers'] });
      setManualForm({ name: '', email: '', phone: '', role: 'viewer', company: '', job_title: '' });
      if (onSuccess) onSuccess();
    },
    onError: (err: any) => {
      setConfirmType(null);
      toast.error(err?.response?.data?.errors?.message || err?.response?.data?.message ||'Gagal menambahkan anggota');
    },
  });

  const handleSendViaEmail = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();

    if (!manualForm.name) {
      toast.error('Nama lengkap wajib diisi');
      return;
    }

    if (!manualForm.email) {
      toast.error('Email wajib diisi');
      return;
    }

    if (!manualForm.role) {
      toast.error('Silakan pilih role/peran untuk anggota ini');
      return;
    }

    setConfirmType('email');
  };

  const handleSendViaWhatsApp = () => {
    if (!manualForm.name) {
      toast.error('Nama lengkap wajib diisi');
      return;
    }

    if (!manualForm.phone) {
      toast.error('Nomor telepon / WhatsApp wajib diisi');
      return;
    }

    const phoneStr = manualForm.phone.trim();
    const cleanPhone = phoneStr.replace(/[^0-9]/g, '');
    const startsWithValidPrefix = phoneStr.startsWith('0') || phoneStr.startsWith('62') || phoneStr.startsWith('+62');

    if (!startsWithValidPrefix) {
      toast.error('Nomor telepon harus diawali dengan 0, 62, atau +62');
      return;
    }

    if (cleanPhone.length < 10 || cleanPhone.length > 14) { // allow 14 digits max just in case some numbers are slightly longer
      toast.error('Panjang nomor telepon harus 10-14 digit angka');
      return;
    }

    if (!manualForm.role) {
      toast.error('Silakan pilih role/peran untuk anggota ini');
      return;
    }

    setConfirmType('whatsapp');
  };

  const executeSendViaWhatsApp = async () => {
    setIsWALoading(true);
    try {
      const res = await addProjectMember(projectId, { ...manualForm, via: 'whatsapp' });
      const invitationData = res?.content?.invitation || res?.content || res?.data?.invitation || res?.data;
      const token = invitationData?.token || '';

      // Format nomor hp (08xxx -> 628xxx)
      let cleanPhone = manualForm.phone.replace(/[^0-9]/g, '');
      if (cleanPhone.startsWith('0')) {
        cleanPhone = '62' + cleanPhone.substring(1);
      }

      const roleObj = CANONICAL_ROLES.find((r) => r.value === manualForm.role);
      const roleLabel = roleObj ? roleObj.label : manualForm.role.toUpperCase();

      const sessionProfileRaw = localStorage.getItem('manproSessionProfile');
      let inviterName = 'Tim Indoteknisi';
      
      if (sessionProfileRaw) {
        try {
          const profile = JSON.parse(sessionProfileRaw);
          inviterName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || inviterName;
        } catch (e) {}
      }

      let waText = '';
      if (token) {
        // KASUS 1: Pengguna Baru / Undangan Pending (Memerlukan Accept Link)
        const acceptUrl = `${window.location.origin}/invitations/accept/${token}`;
        waText = `Halo *${manualForm.name}*,\n\nAnda diundang oleh *${inviterName}* untuk bergabung dalam proyek *${projectName}* sebagai *${roleLabel}*.\n\nSilakan klik tautan berikut untuk menerima undangan & mendaftar:\n${acceptUrl}\n\nTerima kasih,\nTim Indoteknisi MANPRO`;
      } else {
        // KASUS 2: Pengguna Sudah Terdaftar (Langsung Aktif Sebagai Anggota Proyek)
        const projectUrl = `${window.location.origin}/project-management`;
        waText = `Halo *${manualForm.name}*,\n\nAnda telah ditambahkan oleh *${inviterName}* ke dalam proyek *${projectName}* sebagai *${roleLabel}*.\n\nBuka & lihat proyek Anda di MANPRO:\n${projectUrl}\n\nTerima kasih,\nTim Indoteknisi MANPRO`;
      }

      const waUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(waText)}`;
      const waWindow = window.open(waUrl, '_blank');

      if (!waWindow || waWindow.closed || typeof waWindow.closed === 'undefined') {
        toast.error('Browser memblokir popup. Silakan izinkan popup untuk membuka WhatsApp.', { duration: 5000 });
        toast.success('Anggota berhasil ditambahkan ke sistem.', { duration: 3000 });
      } else {
        toast.success('Berhasil menambahkan anggota. Membuka WhatsApp...', { duration: 3000 });
      }

      queryClient.invalidateQueries({ queryKey: ['projectMembers'] });
      setManualForm({ name: '', email: '', phone: '', role: '', company: '', job_title: '' });
      setConfirmType(null);
      if (onSuccess) onSuccess();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Gagal menyimpan data.');
    } finally {
      setIsWALoading(false);
    }
  };

  const marketplaceMut = useMutation({
    mutationFn: async () => {
      if (!marketplaceRole) {
        throw new Error('Silakan pilih role untuk tenaga ahli yang dipilih.');
      }

      const duplicateUsers = selectedUsers.filter((u) => 
        existingMembers.some((m: any) => m.user_id === u.id || (m.contact_snapshot?.email && m.contact_snapshot.email === u.email) || (m.contact_snapshot?.phone && m.contact_snapshot.phone === u.phone))
      );

      if (duplicateUsers.length > 0) {
        const names = duplicateUsers.map(u => u.name).join(', ');
        throw new Error(`Tenaga ahli berikut sudah terdaftar di proyek ini: ${names}`);
      }

      const promises = selectedUsers.map((u) =>
        addMemberFromMarketplace(projectId, {
          user_id: u.id,
          email: u.email,
          phone: u.phone,
          role: marketplaceRole,
        })
      );
      return Promise.all(promises);
    },
    onSuccess: () => {
      toast.success(`${selectedUsers.length} tenaga ahli berhasil ditambahkan`);
      queryClient.invalidateQueries({ queryKey: ['projectMembers'] });
      setSelectedUsers([]);
      if (onSuccess) onSuccess();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || 'Gagal menambahkan anggota');
    },
  });

  const removeSelectedUser = (id: string | number) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== id));
  };

  const isManualLoading = manualMut.isPending || isWALoading;
  const isMarketplaceLoading = marketplaceMut.isPending;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Tab Switcher - Minimalis */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mb-6">
        <button
          type="button"
          onClick={() => setActiveTab('manual')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${
            activeTab === 'manual'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <UserPlus size={16} />
          Manual
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('marketplace')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${
            activeTab === 'marketplace'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Users size={16} />
          Tim Pelaksana
        </button>
      </div>

      {/* Manual Tab */}
      {activeTab === 'manual' && (
        <form className="space-y-4">
          {/* Nama */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Nama Lengkap <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                required
                placeholder="Nama lengkap"
                value={manualForm.name}
                onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition"
              />
            </div>
          </div>

          {/* Email & Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  placeholder="email@domain.com"
                  value={manualForm.email}
                  onChange={(e) => setManualForm({ ...manualForm, email: e.target.value })}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Telepon</label>
              <div className="relative">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  placeholder="0812xxxx"
                  value={manualForm.phone}
                  onChange={(e) => setManualForm({ ...manualForm, phone: e.target.value })}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition"
                />
              </div>
            </div>
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {availableRoles.map((r) => (
                <div
                  key={r.value}
                  onClick={() => setManualForm({ ...manualForm, role: r.value })}
                  className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    manualForm.role === r.value
                      ? 'border-blue-600 bg-blue-50/50 shadow-sm'
                      : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <r.icon size={16} className={manualForm.role === r.value ? 'text-blue-600' : 'text-slate-400'} />
                      <span className={`font-bold text-sm ${manualForm.role === r.value ? 'text-blue-700' : 'text-slate-800'}`}>
                        {r.label}
                      </span>
                    </div>
                    {manualForm.role === r.value && <CheckCircle2 size={16} className="text-blue-600" />}
                  </div>
                  <p className="text-xs text-slate-500 leading-snug pl-6">{r.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Company & Job Title */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Perusahaan</label>
              <div className="relative">
                <Building size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Nama perusahaan"
                  value={manualForm.company}
                  onChange={(e) => setManualForm({ ...manualForm, company: e.target.value })}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Jabatan</label>
              <div className="relative">
                <Briefcase size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Jabatan / posisi"
                  value={manualForm.job_title}
                  onChange={(e) => setManualForm({ ...manualForm, job_title: e.target.value })}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition"
                />
              </div>
            </div>
          </div>

          {/* 2 Pilihan Tombol Pengiriman */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <button
              type="button"
              onClick={handleSendViaEmail}
              disabled={isManualLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Mail size={16} />
              {isManualLoading ? 'Mengirim...' : 'Kirim VIA EMAIL'}
            </button>

            <button
              type="button"
              onClick={handleSendViaWhatsApp}
              disabled={isManualLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition shadow-sm shadow-emerald-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MessageSquare size={16} />
              {isManualLoading ? 'Memproses...' : 'Kirim VIA WA'}
            </button>
          </div>
        </form>
      )}

      {/* Marketplace Tab */}
      {activeTab === 'marketplace' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (selectedUsers.length > 0) marketplaceMut.mutate();
          }}
          className="space-y-4"
        >
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Cari dari Tim Pelaksana Kontraktor Anda
            </label>
            <MarketplaceSearch projectId={projectId} onSelectMulti={(users) => setSelectedUsers(users)} />
          </div>

          {/* Selected Users */}
          {selectedUsers.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-900">
                  <Users size={14} className="inline mr-1.5" />
                  {selectedUsers.length} terpilih
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedUsers([])}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Hapus semua
                </button>
              </div>

              <div className="flex flex-wrap gap-2 max-h-20 overflow-y-auto">
                {selectedUsers.map((u) => (
                  <span
                    key={u.id}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-blue-200 rounded-full text-xs font-medium text-blue-900"
                  >
                    {u.name}
                    <button
                      type="button"
                      onClick={() => removeSelectedUser(u.id)}
                      className="text-gray-400 hover:text-red-500 transition"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role untuk semua <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {availableRoles.map((r) => (
                <div
                  key={r.value}
                  onClick={() => setMarketplaceRole(r.value)}
                  className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    marketplaceRole === r.value
                      ? 'border-blue-600 bg-blue-50/50 shadow-sm'
                      : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <r.icon size={16} className={marketplaceRole === r.value ? 'text-blue-600' : 'text-slate-400'} />
                      <span className={`font-bold text-sm ${marketplaceRole === r.value ? 'text-blue-700' : 'text-slate-800'}`}>
                        {r.label}
                      </span>
                    </div>
                    {marketplaceRole === r.value && <CheckCircle2 size={16} className="text-blue-600" />}
                  </div>
                  <p className="text-xs text-slate-500 leading-snug pl-6">{r.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isMarketplaceLoading || selectedUsers.length === 0 || !marketplaceRole}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle2 size={16} />
            {isMarketplaceLoading 
              ? 'Menambahkan...' 
              : selectedUsers.length > 0 
                ? `Tambahkan ${selectedUsers.length} Anggota` 
                : 'Pilih minimal 1 tenaga ahli'}
          </button>
        </form>
      )}

      {/* Confirmation Modal for Email/WA */}
      <Modal
        isOpen={confirmType !== null}
        onClose={() => setConfirmType(null)}
        title={confirmType === 'whatsapp' ? "Konfirmasi Kirim via WhatsApp" : "Konfirmasi Kirim via Email"}
        size="sm"
        footer={
          <>
            <button
              onClick={() => setConfirmType(null)}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Batal
            </button>
            <button
              onClick={() => {
                if (confirmType === 'whatsapp') {
                  executeSendViaWhatsApp();
                } else if (confirmType === 'email') {
                  manualMut.mutate();
                }
              }}
              disabled={isManualLoading && confirmType === 'email'}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {confirmType === 'whatsapp' ? <MessageSquare size={16} /> : <Mail size={16} />}
              {(isManualLoading && confirmType === 'email') ? 'Mengirim...' : 'Ya, Kirim Sekarang'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Anda akan menambahkan anggota ini dan mengirimkan undangan melalui {confirmType === 'whatsapp' ? 'WhatsApp' : 'Email'}. 
            Apakah data yang Anda masukkan sudah benar?
          </p>
          <div className="bg-slate-50 p-4 rounded-xl space-y-2 border border-slate-100">
            <div className="flex items-center gap-3">
              <User size={16} className="text-slate-400" />
              <span className="text-sm font-medium text-slate-700">{manualForm.name}</span>
            </div>
            {confirmType === 'whatsapp' ? (
              <div className="flex items-center gap-3">
                <Phone size={16} className="text-slate-400" />
                <span className="text-sm text-slate-600">{manualForm.phone}</span>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-slate-400" />
                <span className="text-sm text-slate-600">{manualForm.email}</span>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Shield size={16} className="text-slate-400" />
              <span className="text-sm text-slate-600">{CANONICAL_ROLES.find((r) => r.value === manualForm.role)?.label || manualForm.role}</span>
            </div>
          </div>
        </div>
      </Modal>

    </div>
  );
};